<template>
  <v-card outlined>
    <v-card-title class="primary--text mb-2">
      {{ modalTitle }}
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>{{ closeIcon }}</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-alert
        v-if="!loading && actualizaciones.length === 0"
        dense
        outlined
        type="info"
      >
        No hay actualización registrada para esta versión.
      </v-alert>

      <div v-else>
        <v-row v-if="actualizaciones.length > 1">
          <v-col cols="12" md="5" class="py-0">
            <v-select
              v-model="actualizacionSeleccionadaId"
              :items="actualizaciones"
              item-text="descripcionSeleccion"
              item-value="actualizacionClienteId"
              label="Ejecución"
              outlined
              dense
              @change="loadDetalle"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2" class="py-0">
            <v-text-field
              :value="getTipoEjecucionText(actualizacionSeleccionada)"
              label="Tipo ejecución"
              outlined
              dense
              readonly
              filled
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="3" class="py-0">
            <v-text-field
              :value="actualizacionSeleccionada.estadoDescripcion"
              label="Estado"
              outlined
              dense
              readonly
              filled
            >
              <template v-slot:prepend-inner>
                <v-icon
                  small
                  :color="getEstadoColor(actualizacionSeleccionada)"
                >
                  {{ getEstadoIcon(actualizacionSeleccionada) }}
                </v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="2" class="py-0">
            <v-text-field
              :value="formatDate(actualizacionSeleccionada.fechaInicio)"
              label="Fecha inicio"
              outlined
              dense
              readonly
              filled
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2" class="py-0">
            <v-text-field
              :value="formatDate(actualizacionSeleccionada.fechaFin)"
              label="Fecha fin"
              outlined
              dense
              readonly
              filled
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3" class="py-0">
            <v-text-field
              :value="actualizacionSeleccionada.usuarioNombre"
              label="Usuario"
              outlined
              dense
              readonly
              filled
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2" class="py-0">
            <v-text-field
              :value="formatDate(actualizacionSeleccionada.fechaAlta)"
              label="Fecha alta"
              outlined
              dense
              readonly
              filled
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              :value="actualizacionSeleccionada.mensaje"
              label="Mensaje"
              outlined
              dense
              readonly
              filled
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table
          class="elevation-2 mt-2"
          dense
          :headers="detalleHeaders"
          :items="detalle"
          :loading="loading || loadingDetalle"
          :hide-default-footer="detalle.length > 10 ? false : true"
          item-key="actualizacionClienteDetalleId"
          no-data-text="No hay datos disponibles para mostrar"
        >
          <template v-slot:[`item.estadoDescripcion`]="{ item }">
            <v-icon small :color="getEstadoColor(item)" class="mr-1">
              {{ getEstadoIcon(item) }}
            </v-icon>
            {{ item.estadoDescripcion || item.estadoCodigo }}
          </template>

          <template v-slot:[`item.fechaActualizacion`]="{ item }">
            {{ formatDate(item.fechaActualizacion) }}
          </template>

          <template v-slot:[`item.backupSwitchHabilitado`]="{ item }">
            <v-chip
              x-small
              outlined
              :color="item.backupSwitchHabilitado ? 'success' : 'warning'"
            >
              {{ item.backupSwitchHabilitado ? "Sí" : "No" }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip left v-if="item.resultadoJson">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  size="20"
                  class="mr-2"
                  v-on="on"
                  @click="openResultado(item.resultadoJson)"
                >
                  {{ seeIcon }}
                </v-icon>
              </template>
              <span>Ver resultado</span>
            </v-tooltip>
            <v-tooltip left v-if="item.tieneLogs">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  size="20"
                  v-on="on"
                  @click="openLogs(item)"
                >
                  mdi-script-text-outline
                </v-icon>
              </template>
              <span>Ver logs</span>
            </v-tooltip>
            <v-tooltip
              left
              v-if="isSqlDetalle(item) && !item.backupSwitchHabilitado"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  icon
                  small
                  aria-label="Ver motivo de omisión"
                  v-on="on"
                  @click="openMotivoOmision(item)"
                >
                  <v-icon size="20">mdi-comment-text-outline</v-icon>
                </v-btn>
              </template>
              <span>Ver motivo de omisión</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn outlined @click="$emit('close')">Cerrar</v-btn>
    </v-card-actions>

    <v-dialog v-model="motivoOmisionDialog" max-width="70%" persistent>
      <v-card outlined>
        <v-card-title class="primary--text">
          Motivo de omisión del bakcup SQL
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Cerrar motivo de omisión"
            @click="motivoOmisionDialog = false"
          >
            <v-icon>{{ closeIcon }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <pre class="resultado-json motivo-omision">{{
            motivoOmisionSeleccionado
          }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="logsDialog"
      max-width="75%"
      persistent
      @keydown.esc="logsDialog = false"
    >
      <v-card outlined>
        <v-card-title class="primary--text">
          Logs de scripts
          <v-spacer></v-spacer>
          <v-btn icon @click="logsDialog = false">
            <v-icon>{{ closeIcon }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="logs-card-text">
          <v-text-field
            v-model="logsSearch"
            label="Buscar"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <div class="logs-table-scroll">
            <v-data-table
              class="elevation-2"
              dense
              :headers="logsHeaders"
              :items="logs"
              :loading="loadingLogs"
              :search="logsSearch"
              :hide-default-footer="logs.length > 10 ? false : true"
              :item-class="getLogRowClass"
              item-key="logActualizacionScriptId"
              no-data-text="No hay datos disponibles para mostrar"
            >
              <template v-slot:[`item.nombreScript`]="{ item }">
                <span v-html="highlightLogText(item.nombreScript)"></span>
              </template>
              <template v-slot:[`item.ordenEjecucion`]="{ item }">
                <span v-html="highlightLogText(item.ordenEjecucion)"></span>
              </template>
              <template v-slot:[`item.estadoDescripcion`]="{ item }">
                <v-icon small :color="getEstadoColor(item)" class="mr-1">
                  {{ getEstadoIcon(item) }}
                </v-icon>
                <span
                  v-html="
                    highlightLogText(
                      item.estadoDescripcion || item.estadoCodigo
                    )
                  "
                ></span>
              </template>
              <template v-slot:[`item.fechaEjecucion`]="{ item }">
                <span
                  v-html="highlightLogText(formatDate(item.fechaEjecucion))"
                ></span>
              </template>
              <template v-slot:[`item.resultadoJson`]="{ item }">
                <span v-html="highlightLogText(item.resultadoJson)"></span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="resultadoDialog"
      max-width="70%"
      persistent
      @keydown.esc="resultadoDialog = false"
    >
      <v-card outlined>
        <v-card-title class="primary--text">
          Resultado
          <v-spacer></v-spacer>
          <v-btn icon @click="resultadoDialog = false">
            <v-icon>{{ closeIcon }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <pre class="resultado-json">{{ resultadoSeleccionado }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined @click="resultadoDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import enums from "@/utils/enums/index.js";

export default {
  name: "ActualizacionVersionCliente",
  props: {
    clienteId: {
      type: [Number, String],
      required: true,
    },
    versionId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      closeIcon: enums.icons.CLOSE,
      seeIcon: enums.icons.SEE,
      loading: false,
      loadingDetalle: false,
      loadingLogs: false,
      actualizaciones: [],
      actualizacionSeleccionadaId: null,
      detalle: [],
      logs: [],
      logsSearch: "",
      logsDialog: false,
      motivoOmisionDialog: false,
      motivoOmisionSeleccionado: "",
      resultadoDialog: false,
      resultadoSeleccionado: "",
      detalleHeaders: [
        { text: "Sitio", value: "sitioNombre", sortable: false },
        { text: "Estado", value: "estadoDescripcion", sortable: false },
        {
          text: "Fecha actualización",
          value: "fechaActualizacion",
          sortable: false,
        },
        {
          text: "Origen",
          value: "tipoOrigenDespliegueDescripcion",
          sortable: false,
        },
        { text: "Backup", value: "backupSwitchHabilitado", sortable: false },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
      logsHeaders: [
        { text: "Script", value: "nombreScript", sortable: false },
        { text: "Orden", value: "ordenEjecucion", sortable: false },
        { text: "Estado", value: "estadoDescripcion", sortable: false },
        { text: "Fecha ejecucion", value: "fechaEjecucion", sortable: false },
        { text: "Resultado", value: "resultadoJson", sortable: false },
      ],
    };
  },
  computed: {
    modalTitle() {
      const sistema = this.actualizacionSeleccionada.sistemaNombre;
      const version = this.actualizacionSeleccionada.versionNombre;
      const contexto = [sistema, version].filter(Boolean).join(" - ");

      return contexto
        ? `Actualización de versión - ${contexto}`
        : "Actualización de versión";
    },
    actualizacionSeleccionada() {
      return (
        this.actualizaciones.find(
          (item) =>
            item.actualizacionClienteId === this.actualizacionSeleccionadaId
        ) || {}
      );
    },
  },
  async created() {
    await this.loadActualizaciones();
  },
  methods: {
    isSqlDetalle(item) {
      return (item.tipoDespliegueCodigo || "").toUpperCase() === "SQL_SCRIPTS";
    },
    ...mapActions({
      getActualizacionesClientes: "versiones/getActualizacionesClientes",
      getActualizacionClienteDetalle:
        "versiones/getActualizacionClienteDetalle",
      getLogActualizacionScripts: "versiones/getLogActualizacionScripts",
    }),
    async loadActualizaciones() {
      this.loading = true;
      try {
        const actualizaciones = await this.getActualizacionesClientes({
          clienteId: this.clienteId,
          versionId: this.versionId,
        });
        this.actualizaciones = actualizaciones.map((item) => ({
          ...item,
          descripcionSeleccion: `${this.formatDate(item.fechaAlta)} - ${
            item.estadoDescripcion || item.estadoCodigo
          }`,
        }));

        if (this.actualizaciones.length > 0) {
          this.actualizacionSeleccionadaId =
            this.actualizaciones[0].actualizacionClienteId;
          await this.loadDetalle();
        }
      } finally {
        this.loading = false;
      }
    },
    async loadDetalle() {
      this.detalle = [];
      if (!this.actualizacionSeleccionadaId) return;

      this.loadingDetalle = true;
      try {
        this.detalle = await this.getActualizacionClienteDetalle(
          this.actualizacionSeleccionadaId
        );
      } finally {
        this.loadingDetalle = false;
      }
    },
    async openLogs(item) {
      this.logsDialog = true;
      this.logs = [];
      this.logsSearch = "";
      this.loadingLogs = true;
      try {
        this.logs = await this.getLogActualizacionScripts(
          item.actualizacionClienteDetalleId
        );
      } catch {
        this.logsDialog = false;
      } finally {
        this.loadingLogs = false;
      }
    },
    openMotivoOmision(item) {
      this.motivoOmisionSeleccionado =
        (item.motivoOmisionBackupSql || "").trim() || "Sin motivo registrado";
      this.motivoOmisionDialog = true;
    },
    openResultado(resultadoJson) {
      this.resultadoSeleccionado = this.formatJson(resultadoJson);
      this.resultadoDialog = true;
    },
    formatJson(value) {
      if (!value) return "";
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    },
    getTipoEjecucionText(item) {
      return item?.tipoEjecucionDescripcion || "Sin especificar";
    },
    highlightLogText(value) {
      const text = this.escapeHtml(value);
      const search = (this.logsSearch || "").trim();
      if (!search) return text;

      const regex = new RegExp(`(${this.escapeRegExp(search)})`, "gi");
      return text.replace(
        regex,
        '<mark class="log-search-highlight">$1</mark>'
      );
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    escapeRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },
    getEstadoIcon(item) {
      if (this.isEstadoError(item)) return "mdi-close-circle-outline";

      const codigo = (item?.estadoCodigo || "").toUpperCase();
      const descripcion = (item?.estadoDescripcion || "").toUpperCase();

      if (codigo.includes("FIN") || descripcion.includes("FIN"))
        return "mdi-check-circle-outline";
      return "mdi-progress-clock";
    },
    getEstadoColor(item) {
      if (this.isEstadoError(item)) return "error";

      const codigo = (item?.estadoCodigo || "").toUpperCase();
      const descripcion = (item?.estadoDescripcion || "").toUpperCase();

      if (codigo.includes("FIN") || descripcion.includes("FIN"))
        return "success";
      return "warning";
    },
    isEstadoError(item) {
      const codigo = (item?.estadoCodigo || "").toUpperCase();
      const descripcion = (item?.estadoDescripcion || "").toUpperCase();

      return codigo.includes("ERROR") || descripcion.includes("ERROR");
    },
    getLogRowClass(item) {
      return this.isEstadoError(item) ? "log-row-error" : "";
    },
  },
};
</script>

<style scoped>
.resultado-json {
  background: #f5f5f5;
  border: 1px solid #ddd;
  max-height: 420px;
  overflow: auto;
  padding: 12px;
  white-space: pre-wrap;
}

.motivo-omision {
  overflow-wrap: anywhere;
}

.logs-table-scroll {
  max-height: 420px;
  overflow-y: auto;
}

::v-deep .logs-table-scroll .v-data-table tbody tr.log-row-error > td {
  background-color: #ffcdd3 !important;
}

::v-deep .logs-table-scroll .v-data-table tbody tr.log-row-error:hover > td {
  background-color: #fec0c6 !important;
}

::v-deep .log-search-highlight {
  background-color: #fff176;
  border-radius: 2px;
  padding: 0 1px;
}
</style>
